import {
  MAX_EDIT_BYTES,
  MAX_LIST_ENTRIES,
  buildMentorContext,
  conceptMoveFor,
  gatherMentorEvidence,
  hiddenSeedValues,
  isEditableText,
  publicMission,
  resolveMissionPath,
  summarize,
  utf8Length,
  type Session,
  submissionExportScript,
  validateSubmission,
  pilotMission,
  benchMission,
  SUBMISSION_MAX_BYTES,
  q,
  checkCheckpointPractice,
  inTurn,
  type Submission,
  type Workspace,
} from '@engineer-os/engine';
import { activeRuntime, benchMissions, bootRuntime, missionCatalog, type Runtime } from './engine/runtime';
import type { Resumed } from './engine/keeper';
import type { FileEntry, GitState, GradeResult, Hint, MentorContext, PublicMission, SessionSummary } from './types';

/**
 * The twelve calls the screens make, answered in this tab.
 *
 * They were eleven HTTP routes and one `fetch` helper; they are now the same
 * functions over the engine running in the page (web/src/engine/
 * runtime.ts), returning exactly what the routes returned. That is the whole
 * of the client's migration: no component knows the server is gone.
 *
 * Failures keep the shape the routes gave them — an `Error` carrying the
 * status the route would have used — because callers read it. `useSession`
 * tells "this session no longer exists" (404, start the level again) from
 * every other failure by that number alone, and the engine's own file API
 * already throws 400/403 errors of the same shape.
 *
 * Those messages stay English in both languages. They are a route's reply to
 * its caller — a session id, a path, a byte limit, a status — and the number
 * beside them, not the sentence, is what the interface branches on. The two
 * thrown sentences a learner actually reads are in engine/runtime.ts and come
 * from the catalogue (web/src/lib/strings/boot.ts, `runtime`).
 *
 * `pullRequests` is the one call here that never was a route. There is no code
 * host to ask: the bare repository beside the working copy *is* the server
 * (docs/github.md), so the call is a command run in the workspace like any
 * other, and what it parses is the JSON that lives in it.
 */

function httpError(status: number, message: string): Error {
  return Object.assign(new Error(message), { status });
}

/** Where the editor's file calls read and write: a level's session. */
async function filesOf(id: string): Promise<{ workspace: Workspace; cwd: string; saved(rel: string): void }> {
  const { runtime, session } = await withSession(id);
  return {
    workspace: session.workspace,
    cwd: session.mission.cwd,
    // A save is work the prompt never sees; without this, a level solved
    // entirely in the editor would look like a clear with no commands.
    saved: (rel) => runtime.sessions.recordSave(session, rel),
  };
}

/** Wait for the VM, then look the session up. The 404 is load-bearing; see above. */
async function withSession(id: string): Promise<{ runtime: Runtime; session: Session }> {
  const runtime = await bootRuntime();
  const session = runtime.sessions.get(id);
  if (!session) throw httpError(404, `session "${id}" not found`);
  return { runtime, session };
}

/* --- The code host, offline -----------------------------------------------

   docs/github.md: a pull request is a file — `pulls/N.json` inside the bare
   repository the branch is reviewed in. Everything below reads those files and
   the two things they do not contain: the commits on the branch, and the files
   they touched, which are questions only git can answer. */

/** One review left on a pull request. `state` follows the real `gh` field. */
export interface PullRequestReview {
  author: string;
  state: 'approved' | 'changes_requested' | 'commented' | string;
  body: string;
  at: string;
}

/** One comment. `path` and `line` are null when it is about the branch, not a line. */
export interface PullRequestComment {
  id: number;
  author: string;
  path: string | null;
  line: number | null;
  body: string;
  resolved: boolean;
  at: string;
}

/** One check result, written by the pre-receive hook or by a seed. */
export interface PullRequestCheck {
  name: string;
  status: 'pass' | 'fail' | 'pending' | string;
  detail: string;
}

/** One commit the pull request brings. Read from git, not from the JSON. */
export interface PullRequestCommit {
  hash: string;
  short: string;
  subject: string;
  author: string;
  /** Unix seconds, as `%at` gives them. */
  time: number;
}

/** One file the pull request touches. `status` is git's own letter: A, M, D, R100. */
export interface PullRequestFile {
  status: string;
  path: string;
  /** Where a renamed or copied file came from, else null. */
  from: string | null;
}

/**
 * A pull request, as the pane reads it.
 *
 * The first block is the JSON on disk, field for field. `commits` and `files`
 * are computed here: they are `git log base..head` and `git diff base...head`
 * in whichever repository holds both refs, which is the base repository in the
 * ordinary case and the fork when the level made one. `checks` is the file's
 * own, with anything the pre-receive hook recorded against the head commit
 * merged over it by name — the same merge `gh` performs.
 */
export interface PullRequest {
  number: number;
  title: string;
  body: string;
  head: string;
  base: string;
  state: 'open' | 'merged' | 'closed' | string;
  draft: boolean;
  author: string;
  created: string;
  head_sha: string | null;
  reviewers: string[];
  reviews: PullRequestReview[];
  comments: PullRequestComment[];
  checks: PullRequestCheck[];
  merged_sha: string | null;
  merge_method: string | null;
  commits: PullRequestCommit[];
  files: PullRequestFile[];
}

/**
 * Find the base repository, read every pull request in it, and ask git what is
 * on each branch.
 *
 * It resolves the repository the way `gh` does and the way every check in
 * docs/github.md §6 does: `upstream` when that remote exists — you are on a
 * fork — otherwise `origin`. A remote with a scheme is somebody else's
 * machine, and there is no network here, so it is left alone.
 *
 * Cheap where there is nothing to find, which is most levels: four commands
 * and a missing directory, and it is gone. The git work happens once per pull
 * request and only for a pull request that exists.
 */
const PULLS_SCRIPT = [
  'git rev-parse --is-inside-work-tree >/dev/null 2>&1 || exit 0',
  'top=$(git rev-parse --show-toplevel 2>/dev/null) || exit 0',
  'cd "$top" || exit 0',
  'base=$(git remote get-url upstream 2>/dev/null) || base=$(git remote get-url origin 2>/dev/null) || exit 0',
  '[ -n "$base" ] || exit 0',
  'case "$base" in *://*) exit 0 ;; esac',
  'case "$base" in /*) ;; *) base=$top/$base ;; esac',
  '[ -d "$base/pulls" ] || exit 0',
  // jq is what the offline `gh` itself is written against, so it is there.
  // The fallback exists because this pane must not be the one thing that
  // breaks when it is not: two fields out of a flat object is a job sed can
  // do, and getting them wrong costs the commit list, not the pull request.
  'jq_ok=0',
  'command -v jq >/dev/null 2>&1 && jq_ok=1',
  'field() {',
  '  if [ "$jq_ok" = 1 ]; then jq -r --arg k "$1" \'.[$k] // empty\' "$2" 2>/dev/null; return; fi',
  '  sed -n \'s/.*"\'"$1"\'"[[:space:]]*:[[:space:]]*"\\([^"]*\\)".*/\\1/p\' "$2" | head -n 1',
  '}',
  // A branch may be a local head here, a remote-tracking ref there, or gone
  // entirely after `--delete-branch`. Each spelling is tried in turn, and the
  // recorded head_sha is the last resort.
  'resolve() {',
  '  git -C "$1" rev-parse -q --verify "refs/heads/$2^{commit}" 2>/dev/null && return 0',
  '  git -C "$1" rev-parse -q --verify "refs/remotes/origin/$2^{commit}" 2>/dev/null && return 0',
  '  git -C "$1" rev-parse -q --verify "refs/remotes/upstream/$2^{commit}" 2>/dev/null && return 0',
  '  git -C "$1" rev-parse -q --verify "$2^{commit}" 2>/dev/null && return 0',
  '  return 1',
  '}',
  'for f in "$base"/pulls/*.json; do',
  '  [ -f "$f" ] || continue',
  '  h=$(field head "$f")',
  '  b=$(field base "$f")',
  '  hsha=$(field head_sha "$f")',
  '  msha=$(field merged_sha "$f")',
  '  repo=',
  '  for r in "$base" "$top"; do',
  '    hs=$(resolve "$r" "$h") || hs=$(git -C "$r" rev-parse -q --verify "$hsha^{commit}" 2>/dev/null) || hs=',
  '    [ -n "$hs" ] || continue',
  '    bs=$(resolve "$r" "$b") || bs=',
  '    [ -n "$bs" ] || continue',
  '    repo=$r',
  '    break',
  '  done',
  '  printf "@@PR\\n"',
  '  cat "$f"',
  // What the hook recorded against the head commit, which `gh` folds into the
  // file only the next time it is asked. A learner who has pushed and not run
  // `gh pr view` has still had a check run, and this pane is looking at the
  // state rather than at the transcript — the same call the grader makes
  // (packages/engine/src/grader/checks.ts, `shaChecks`).
  '  printf "\\n@@HOOKCHECKS\\n"',
  '  [ -n "$hsha" ] && cat -- "$base/pulls/checks/$hsha.json" 2>/dev/null',
  '  printf "\\n@@COMMITS\\n"',
  '  if [ -n "$repo" ]; then',
  // A merged pull request is an ancestor of its own base, so `base..head` is
  // empty exactly when there is most to show. The merge commit still knows:
  // first parent is where the base was, second is the branch.
  '    if [ -n "$msha" ] && git -C "$repo" rev-parse -q --verify "$msha^2^{commit}" >/dev/null 2>&1; then',
  '      lo="$msha^1"; hi="$msha^2"',
  '    else',
  '      lo=$bs; hi=$hs',
  '    fi',
  '    git -C "$repo" log --format="%H%x1f%h%x1f%s%x1f%an%x1f%at" -n 60 "$lo..$hi" 2>/dev/null',
  '    printf "@@FILES\\n"',
  '    git -C "$repo" diff --name-status -M "$lo...$hi" 2>/dev/null',
  '  else',
  '    printf "@@FILES\\n"',
  '  fi',
  'done',
  'printf "@@END\\n"',
].join('\n');

const US = '\x1f';

/** Anything at all, as long as it is a string. Used on every field of the JSON. */
function str(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function list<T>(value: unknown, map: (item: Record<string, unknown>) => T): T[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is Record<string, unknown> => typeof v === 'object' && v !== null).map(map);
}

/**
 * One pull request, from whatever the file actually held.
 *
 * Defensive on every field on purpose. The JSON is seeded by a level, written
 * by a bash `gh`, and — the case that matters — editable by the learner, whose
 * hand-written pull request is a legitimate way to pass the objective
 * (docs/github.md §1). A missing field is a field the pane does not draw, never
 * a pane that does not draw.
 */
function parsePull(raw: Record<string, unknown>): PullRequest {
  return {
    number: typeof raw.number === 'number' ? raw.number : 0,
    title: str(raw.title),
    body: str(raw.body),
    head: str(raw.head),
    base: str(raw.base),
    state: str(raw.state, 'open'),
    draft: raw.draft === true,
    author: str(raw.author),
    created: str(raw.created),
    head_sha: typeof raw.head_sha === 'string' ? raw.head_sha : null,
    reviewers: Array.isArray(raw.reviewers) ? raw.reviewers.filter((r): r is string => typeof r === 'string') : [],
    reviews: list(raw.reviews, (r) => ({
      author: str(r.author),
      state: str(r.state, 'commented'),
      body: str(r.body),
      at: str(r.at),
    })),
    comments: list(raw.comments, (c) => ({
      id: typeof c.id === 'number' ? c.id : 0,
      author: str(c.author),
      path: typeof c.path === 'string' ? c.path : null,
      line: typeof c.line === 'number' ? c.line : null,
      body: str(c.body),
      resolved: c.resolved === true,
      at: str(c.at),
    })),
    checks: list(raw.checks, (c) => ({
      name: str(c.name),
      status: str(c.status, 'pending'),
      detail: str(c.detail),
    })),
    merged_sha: typeof raw.merged_sha === 'string' ? raw.merged_sha : null,
    merge_method: typeof raw.merge_method === 'string' ? raw.merge_method : null,
    commits: [],
    files: [],
  };
}

/** `M<TAB>path`, or `R100<TAB>old<TAB>new` for a rename. */
function parseFileLine(line: string): PullRequestFile | null {
  const parts = line.split('\t');
  const status = parts[0];
  if (!status) return null;
  if (parts.length >= 3 && (status.startsWith('R') || status.startsWith('C'))) {
    const path = parts[2];
    if (!path) return null;
    return { status, path, from: parts[1] ?? null };
  }
  const path = parts[1];
  if (!path) return null;
  return { status, path, from: null };
}

/**
 * The checks the hook wrote under the head commit, over the ones in the file.
 *
 * The same merge `gh` does, by name and newest run first: a check the file
 * already carries is the older record of the same run.
 */
function mergeChecks(fileChecks: PullRequestCheck[], hook: string): PullRequestCheck[] {
  if (!hook.trim()) return fileChecks;
  let parsed: unknown;
  try {
    parsed = JSON.parse(hook);
  } catch {
    return fileChecks;
  }
  if (typeof parsed !== 'object' || parsed === null) return fileChecks;
  const recorded = list((parsed as Record<string, unknown>).checks, (c) => ({
    name: str(c.name),
    status: str(c.status, 'pending'),
    detail: str(c.detail),
  }));
  if (recorded.length === 0) return fileChecks;
  const byName = new Map<string, PullRequestCheck>();
  for (const check of fileChecks) byName.set(check.name, check);
  for (const check of recorded) byName.set(check.name, check);
  return [...byName.values()];
}

/** Split the script's output back into pull requests. A record the parser cannot read is dropped, not thrown. */
function parsePulls(raw: string): PullRequest[] {
  const out: PullRequest[] = [];
  let json: string[] | null = null;
  let hook: string[] | null = null;
  let commits: string[] | null = null;
  let files: string[] | null = null;

  const flush = (): void => {
    if (!json) return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(json.join('\n'));
    } catch {
      json = null;
      return;
    }
    if (typeof parsed === 'object' && parsed !== null) {
      const pull = parsePull(parsed as Record<string, unknown>);
      pull.checks = mergeChecks(pull.checks, (hook ?? []).join('\n'));
      pull.commits = (commits ?? []).map((line) => {
        const [hash = '', short = '', subject = '', author = '', at = '0'] = line.split(US);
        return { hash, short, subject, author, time: Number(at) || 0 };
      });
      pull.files = (files ?? []).map(parseFileLine).filter((f): f is PullRequestFile => f !== null);
      out.push(pull);
    }
    json = null;
    hook = null;
    commits = null;
    files = null;
  };

  for (const line of raw.split(/\r?\n/)) {
    if (line === '@@PR') {
      flush();
      json = [];
    } else if (line === '@@HOOKCHECKS') {
      hook = [];
    } else if (line === '@@COMMITS') {
      commits = [];
    } else if (line === '@@FILES') {
      files = [];
    } else if (line === '@@END') {
      flush();
    } else if (files) {
      if (line) files.push(line);
    } else if (commits) {
      if (line) commits.push(line);
    } else if (hook) {
      hook.push(line);
    } else if (json) {
      json.push(line);
    }
  }
  flush();

  // Newest first, the way the host this imitates lists them.
  return out.sort((a, b) => b.number - a.number);
}

export interface SessionBundle {
  session: SessionSummary;
  mission: PublicMission;
  state: GitState;
  hints: Hint[];
  lastGrade: GradeResult | null;
  reflection: string | null;
  /**
   * Set only by `createSession`, when this open found the learner's
   * unfinished work: brought back, or dropped because the level changed
   * (engine/keeper.ts). Absent on a re-attach, which found nothing new.
   */
  resumed?: Resumed;
}

/* --- What the online mentor is told ---------------------------------------

   `buildMentorContext` decides the shape and does the capping
   (packages/engine/src/mentor-context.ts); everything below is the tab
   answering the questions that shape asks, out of the session it already
   holds. There is still no record of typed commands — `commandDone` does not
   keep one — so what the learner typed arrives only as the terminal showed
   it, read off the screen by the caller at the moment of asking. */

/** Telemetry events of one type, counted. The session keeps them; nobody else does. */
function counted(session: Session, type: 'command' | 'save'): number {
  return session.telemetry.filter((e) => e.type === type).length;
}

/**
 * The grade a question is asked against, and whose it is.
 *
 * The learner's own Check while nothing has moved the tree since it, and a
 * fresh look otherwise (`SessionManager.gradeForMentor`, which never counts,
 * records or completes anything). A level this tab cannot grade — a
 * checkpoint, graded on a server — or a workspace that stopped answering
 * gives no grade at all rather than the last Check: that one may describe a
 * tree that is gone, and a stale grade stated as the present is the bug this
 * exists to end.
 */
async function mentorGrade(runtime: Runtime, s: Session): Promise<{ grade: GradeResult | null; source?: 'check' | 'mentor' }> {
  try {
    return await runtime.sessions.gradeForMentor(s);
  } catch {
    return { grade: null };
  }
}

/**
 * The file the learner is writing, on the levels whose work is a document.
 *
 * Which file is not authored anywhere, and it does not need to be: the levels
 * this applies to are graded on something the learner wrote, so the last thing
 * they saved from the editor *is* the work. Nothing is read on any other kind
 * of level, and nothing is read at all until a save has happened.
 *
 * Every failure is an absent excerpt rather than a thrown request. A document
 * that cannot be read is a mentor turn with one fewer fact in it, which is a
 * worse answer and not a broken one.
 */
async function documentUnderReview(session: Session): Promise<{ path: string; excerpt: string } | undefined> {
  const saves = session.telemetry.filter((e) => e.type === 'save' && typeof e.data === 'string');
  const last = saves[saves.length - 1];
  if (!last) return undefined;
  try {
    const { rel, full } = resolveMissionPath(session.mission.cwd, last.data as string);
    if (!rel) return undefined;
    const content = await session.workspace.readFile(full);
    if (content === null || !isEditableText(content)) return undefined;
    return { path: rel, excerpt: content };
  } catch {
    return undefined;
  }
}

export const api = {
  checkpointRevision: async (id: string): Promise<number> => {
    const { session } = await withSession(id);
    return session.telemetry.filter(e => e.type === 'command' || e.type === 'save' || e.type === 'reset').length;
  },
  checkpointPractice: async (id: string) => {
    const { session } = await withSession(id);
    return inTurn(session.workspace, () => checkCheckpointPractice(session.workspace, session.mission));
  },
  exportSubmission: async (id: string): Promise<Submission> => {
    const { session } = await withSession(id);
    // A study benchmark task is graded on the verifier like a checkpoint (docs/efficiency-bench.md).
    if (!pilotMission(session.mission.id) && !benchMission(session.mission.id)) throw httpError(400, 'Verification is unavailable for this mission');
    const snapshot = `/tmp/eos-submission-${crypto.randomUUID()}.json`;
    try {
      const result = await session.workspace.exec('node --input-type=commonjs', { input: submissionExportScript(session.mission.id, snapshot), timeoutMs: 60_000 });
      if (result.code !== 0) throw httpError(400, 'The workspace could not be submitted. Check file sizes and symbolic links.');
      const length = Number(result.stdout);
      if (!Number.isSafeInteger(length) || length <= 0 || length > SUBMISSION_MAX_BYTES) throw httpError(413, 'Submission is too large');
      // Each guest response is capped at 1 MiB. ASCII JSON lets byte chunks
      // cross serial replies without splitting a multibyte path character.
      let text = '';
      for (let offset = 0; offset < length; offset += 65536) {
        const chunk = await session.workspace.exec(`dd if=${q(snapshot)} bs=65536 skip=${offset / 65536} count=1 2>/dev/null`);
        if (chunk.code !== 0) throw httpError(400, 'Could not read submission');
        text += chunk.stdout;
      }
      if (text.length !== length) throw httpError(400, 'Incomplete submission');
      const submission: unknown = JSON.parse(text);
      const problem = validateSubmission(submission);
      if (problem) throw httpError(400, problem);
      return submission as Submission;
    } finally { await session.workspace.exec(`rm -f -- ${q(snapshot)}`).catch(() => {}); }
  },
  health: async (): Promise<{ ok: boolean; driver: string; missions: number; sessions: number }> => {
    const missions = await missionCatalog();
    // The driver used to name the machine the learner's bash ran on; it runs
    // here now, in the same words the level list uses. Asking for this must
    // not start a 13 MB download, so the VM is counted only if it is already
    // up.
    return { ok: true, driver: 'Linux in this tab', missions: missions.size, sessions: activeRuntime()?.sessions.list().length ?? 0 };
  },

  // The course: never a bench task, which the runtime's Map holds once the
  // study's tasks are loaded (engine/runtime.ts, `benchMissions`).
  listMissions: async (): Promise<PublicMission[]> =>
    [...(await missionCatalog()).values()].filter((m) => m.track !== 'bench').map(publicMission),

  /** The efficiency benchmark's tasks, signed in only (docs/efficiency-bench.md). */
  listBenchMissions: async (): Promise<PublicMission[]> => (await benchMissions()).map(publicMission),

  getMission: async (id: string): Promise<PublicMission> => {
    const mission = (await missionCatalog()).get(id);
    if (!mission) throw httpError(404, 'mission not found');
    return publicMission(mission);
  },

  createSession: async (missionId: string): Promise<SessionBundle> => {
    const runtime = await bootRuntime();
    if (!runtime.missions.has(missionId)) throw httpError(404, 'mission not found');
    const { session: s, state, resumed } = await runtime.start(missionId);
    return {
      session: summarize(s),
      mission: publicMission(s.mission),
      // The read `start` already made, rather than a second one of a
      // repository that cannot have changed in between: the level is open,
      // the terminal is not attached yet, and nobody has typed anything. The
      // fallback is for the one case `start` tolerates and this does not —
      // a read that failed leaves no evidence to judge but still owes the
      // screen a state, so it is asked for again and fails the open here.
      state: state ?? (await runtime.sessions.state(s)),
      hints: [],
      lastGrade: null,
      reflection: null,
      resumed,
    };
  },

  getSession: async (id: string): Promise<SessionBundle> => {
    const { runtime, session: s } = await withSession(id);
    return {
      session: summarize(s),
      mission: publicMission(s.mission),
      state: await runtime.sessions.state(s),
      hints: runtime.sessions.revealedHints(s),
      lastGrade: s.lastGrade,
      reflection: runtime.sessions.reflection(s),
    };
  },

  /**
   * The words of an open level, in whatever language the catalogue is in now.
   *
   * `getSession` less the two reads that cost something: the repository and
   * the grade. A language switch rewrites the mission objects in place, and
   * the session's mission is one of them, so these three come back in the new
   * language with no round trip into the guest. The grade is not among them
   * because its failure messages were produced by running checks — they say
   * what the tree looked like at that check — and only the next Check can
   * say it again.
   */
  sessionText: async (id: string): Promise<Pick<SessionBundle, 'mission' | 'hints' | 'reflection'>> => {
    const { runtime, session: s } = await withSession(id);
    return {
      mission: publicMission(s.mission),
      hints: runtime.sessions.revealedHints(s),
      reflection: runtime.sessions.reflection(s),
    };
  },

  getState: async (id: string): Promise<GitState> => {
    const { runtime, session } = await withSession(id);
    return runtime.sessions.state(session);
  },

  check: async (id: string): Promise<{ result: GradeResult; session: SessionSummary; reflection: string | null }> => {
    const { runtime, session: s } = await withSession(id);
    // The runtime, not the session manager: a pass in this tab also carries
    // the plausibility flags the grader itself cannot compute (runtime.ts).
    const result = await runtime.check(s);
    return { result, session: summarize(s), reflection: runtime.sessions.reflection(s) };
  },

  hint: async (id: string): Promise<{ level: number; hints: Hint[]; hint_count: number }> => {
    const { runtime, session: s } = await withSession(id);
    return { ...runtime.sessions.hint(s), hint_count: s.mission.hints.length };
  },

  /**
   * Which named wrong state this workspace is in, or null.
   *
   * The expensive one, and the only call in this file whose cost is the reason
   * it exists as a call at all. Matching runs every recognizer the level
   * authored, in author order, until one hits — up to seven more round trips
   * into the emulator on a level with seven of them — so it is not folded into
   * `check`, where it would be paid for by everybody including the learner who
   * is checking because they are making progress.
   *
   * It has one reader left and it is not a nudge. The panel that used to name
   * the state out loud is gone; what is still true of a match is that the
   * workspace demonstrates what the first rungs of the ladder would have said,
   * which is what `free_through` prices (docs/mentor.md), and that a matched id
   * is a fact worth putting in front of the model. Null is the ordinary answer
   * and costs the learner nothing either way.
   */
  situation: async (id: string): Promise<{ situation: string | null }> => {
    const { runtime, session: s } = await withSession(id);
    return { situation: await runtime.sessions.matchSituation(s) };
  },

  /**
   * Everything the online mentor is ever told about one learner.
   *
   * The one call in this file whose answer leaves the tab. It is built here
   * rather than in the component because the shape belongs to the engine and
   * is parsed at the other end by the Worker (docs/mentor-plan.md §5.3), and
   * because half of what goes into it — telemetry counts, rungs opened, how
   * long this level has been open — is session state no screen holds.
   *
   * The grade is current, not merely the last one (`mentorGrade`): the Check
   * the learner pressed when nothing has run or been saved since, otherwise a
   * look taken now. With it go the evidence — the files the failing checks
   * and git point at, gathered in one command (`gatherMentorEvidence`) — and
   * `terminal`, the screen's newest text, which only the caller can read.
   *
   * ## Which named wrong state goes with it
   *
   * Two claims arrive from the caller, and they are passed apart because they
   * are true for different lengths of time:
   *
   * - `choice` is the state a choice the learner just picked belongs to. The
   *   question is about that state whatever the tree says now, so it always
   *   wins, and nothing is matched.
   * - `matched` is what the recognizers said on the failing path of the
   *   learner's last Check. It is a fact about the tree that Check graded,
   *   so it stands exactly as long as that Check is the grade — while nothing
   *   has run, been saved or been reset since (`source: 'check'`).
   *
   * When the grade is taken fresh for this question (`source: 'mentor'`),
   * `matched` describes a tree that has moved, and is dropped. It used to be
   * kept: a learner matched into a conflict who resolved it and asked without
   * pressing Check was told they were still in it, in the block the prompt
   * calls true now, and in the dedupe key. The fresh look has already paid to
   * see the workspace, so when it fails the recognizers are run again — a
   * named wrong state is the most specific fact the level can offer the model
   * — and when it passes there is no wrong state to name.
   *
   * With no grade at all, nothing is claimed: a remembered match with no grade
   * behind it is the stale grade by another name.
   *
   * `lastReplies` was the second option and is gone. It carried the last three
   * of the model's own sentences so the prompt could ask it not to repeat
   * itself — half a conversation, unlabelled as to who said what, which was
   * the best that could be done while a turn was the unit. The tab now posts
   * the transcript, so the field is deprecated and ignored at both ends
   * (`MentorContextInput.lastReplies`, `MAX_REPLIES`) and nothing fills it.
   * Two memories of one conversation is two things that have to agree.
   */
  mentorContext: async (
    id: string,
    options: { choice?: string | null; matched?: string | null; terminal?: string } = {},
  ): Promise<MentorContext> => {
    const { runtime, session: s } = await withSession(id);
    const state = await runtime.sessions.state(s);
    const { grade, source } = await mentorGrade(runtime, s);
    let situation: string | null = null;
    if (options.choice) situation = options.choice;
    else if (source === 'check') situation = options.matched ?? null;
    else if (source === 'mentor' && grade && !grade.passed && (s.mission.situations?.length ?? 0) > 0) {
      situation = await runtime.sessions.matchSituation(s);
    }
    // Whether this level's work is a document is not a field on the mission,
    // it is what the level's concept move comes out as, and the engine's move
    // table is the one place that decides. Pure, and a lookup rather than a
    // second table kept here.
    const writing = conceptMoveFor(s.mission)?.kind === 'writing';
    const document = writing ? await documentUnderReview(s) : undefined;
    // The document is the fuller copy of its own file, so the evidence does
    // not pay for it a second time.
    const files = (await gatherMentorEvidence(s.mission, s.workspace, state, grade, s.seed, s.startHead))
      .filter((f) => f.path !== document?.path);
    return buildMentorContext({
      state,
      grade,
      ...(source ? { gradeSource: source } : {}),
      situation,
      checks: s.checks,
      hintsUsed: s.hintLevel,
      commands: counted(s, 'command'),
      saves: counted(s, 'save'),
      minutes: (Date.now() - s.createdAt) / 60_000,
      ...(options.terminal ? { terminal: options.terminal } : {}),
      ...(files.length > 0 ? { files } : {}),
      ...(document ? { document } : {}),
    });
  },

  /**
   * The seed values this learner has to find and has not been shown, which
   * the Worker's output filter holds back from every reply
   * (packages/engine/src/mentor-hidden.ts).
   *
   * A call of its own rather than a field of `mentorContext`, and that is the
   * point of it: the context is everything the model is told, and these must
   * never reach the model — a list of what not to say is a list of the
   * answers. The tab is where they are computed because it is the only party
   * holding both the level and what its workspace was seeded with; the
   * Worker's bundles carry `{{seed.KEY}}` and nothing behind it.
   *
   * Pure after the session lookup: no round trip into the guest, so it costs
   * a question nothing. The mission is the one on screen, in whichever
   * language is on, because what counts as shown is what the learner read.
   */
  mentorHidden: async (id: string): Promise<string[]> => {
    const { session: s } = await withSession(id);
    return hiddenSeedValues(s.mission, s.seed);
  },

  reset: async (id: string): Promise<{ session: SessionSummary; state: GitState }> => {
    const { runtime } = await withSession(id);
    const s = await runtime.sessions.reset(id);
    // After the reset, not before: a copy in flight across it is then
    // discarded rather than landing on top of the fresh start (keeper.ts).
    await runtime.forgetWork(s.mission.id);
    return { session: summarize(s), state: await runtime.sessions.state(s) };
  },

  /**
   * The guest said a command finished at the prompt. The server learned the
   * command text from the bytes it relayed; here the shell's PROMPT_COMMAND
   * sends one byte per prompt and nothing else, so the session records that
   * a command ran, not which. The count is what plausibility reads.
   */
  commandDone: async (id: string): Promise<void> => {
    const { runtime, session } = await withSession(id);
    runtime.sessions.recordCommand(session, '');
  },

  /**
   * A command Iris proposed and the learner approved with Run it
   * (components/play/Iris.tsx, docs/iris.md "The panel").
   *
   * In the mission's directory, as the terminal starts there, and outside the
   * grading queue (`inTurn`) on purpose: it is the learner's work, like a
   * command typed at the prompt, and a Check pressed while it runs should
   * meet it exactly as it would meet a typed one.
   *
   * Recorded as a command, and without its text, the way `commandDone`
   * records a typed one: plausibility and a checkpoint's revision count work,
   * and approving a step is work. The text stays out so that the session's
   * record says the same thing about both routes.
   *
   * A command that outlives the minute comes back as exit 124 with a line
   * saying so, which is what `timeout` itself prints, rather than as a
   * failure: Iris is owed the fact, and the learner is owed the panel going on.
   * Anything else — a workspace that is gone, a command too long for the
   * guest's line — throws, and the panel says the step could not be run.
   */
  irisRun: async (id: string, command: string): Promise<{ stdout: string; stderr: string; code: number }> => {
    const { runtime, session } = await withSession(id);
    runtime.sessions.recordCommand(session, '');
    try {
      return await session.workspace.exec(command, { cwd: session.mission.cwd, timeoutMs: 60_000 });
    } catch (err) {
      if ((err as { code?: unknown }).code !== 'timeout') throw err;
      return { stdout: '', stderr: 'The command was stopped after 60 seconds.', code: 124 };
    }
  },

  /* --- Workspace files, for the in-browser editor ------------------------- */

  listFiles: async (id: string, path = ''): Promise<FileEntry[]> => {
    const { workspace, cwd } = await filesOf(id);
    const { full } = resolveMissionPath(cwd, path);
    return workspace.listFiles(full, { maxEntries: MAX_LIST_ENTRIES });
  },

  readFile: async (id: string, path: string): Promise<{ path: string; content: string }> => {
    const { workspace, cwd } = await filesOf(id);
    const { rel, full } = resolveMissionPath(cwd, path);
    if (!rel) throw httpError(400, 'path query parameter is required');
    const content = await workspace.readFile(full);
    if (content === null) throw httpError(404, `file "${rel}" not found`);
    if (!isEditableText(content)) {
      throw httpError(415, `file "${rel}" is not editable text (binary, or larger than ${MAX_EDIT_BYTES} bytes)`);
    }
    return { path: rel, content };
  },

  writeFile: async (id: string, path: string, content: string): Promise<{ ok: true }> => {
    const { workspace, cwd, saved } = await filesOf(id);
    const { rel, full } = resolveMissionPath(cwd, path);
    if (!rel) throw httpError(400, 'path is required');
    if (typeof content !== 'string') throw httpError(400, 'content must be a string');
    if (utf8Length(content) > MAX_EDIT_BYTES) throw httpError(413, `content is larger than ${MAX_EDIT_BYTES} bytes`);
    await workspace.writeFile(full, content);
    saved(rel);
    return { ok: true };
  },

  /* --- The pull requests open on this workspace --------------------------- */

  /**
   * Every pull request in the base repository, newest first.
   *
   * Runs in the mission working directory, exactly as the git graph does
   * (`extractGitState`), because that is where the remotes are. An empty list
   * is the ordinary answer: no repository, no remote, no `pulls/` directory,
   * or none opened yet. Only a workspace that cannot run a command at all is
   * an error, and it arrives as one.
   */
  pullRequests: async (id: string): Promise<PullRequest[]> => {
    const { session } = await withSession(id);
    const r = await session.workspace.exec(PULLS_SCRIPT, { cwd: session.mission.cwd, timeoutMs: 20_000 });
    return parsePulls(r.stdout);
  },
};
